import React, { useCallback, useEffect, useState } from 'react'

import { Box, IconButton, Typography } from '@material-ui/core'
import { ArrowBack } from '@material-ui/icons'
import { isEmpty } from 'lodash'
import DOMPurify from 'dompurify'

import { LoadingFeedback } from 'components'

import {
  ButtonAction,
  LessonSidebar,
  LessonAttachments,
  LessonPlayer,
} from './components'

import useAuthTraining from 'hooks/useAuthTraining'
import useSnackbar from 'hooks/useSnackbar'
import helpers from 'helpers'
import * as service from 'service'
import { routes } from 'TrainingRoutes'
import useStyles from './styles'

const LessonContent = ({ match, history }) => {
  const classes = useStyles()
  const snackbar = useSnackbar()
  const { portalToken: contextPortalToken } = useAuthTraining()

  const portalToken = match?.params?.portalToken || contextPortalToken
  const courseId = match?.params?.courseId
  const lessonId = match?.params?.lessonId

  const [lesson, setLesson] = useState(null)
  const [modules, setModules] = useState([])
  const [courseTitle, setCourseTitle] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isCompleting, setIsCompleting] = useState(false)

  const loadData = useCallback(async () => {
    setIsLoading(true)
    try {
      const [lessonResponse, modulesResponse] = await Promise.all([
        service.trainingPortal.courses.lesson(courseId, lessonId),
        service.trainingPortal.courses.modules(courseId),
      ])
      setLesson(lessonResponse?.data?.lesson || null)
      setModules(modulesResponse?.data?.modules || [])

      const courseResponse = await service.trainingPortal.courses.show(courseId)
      setCourseTitle(courseResponse?.data?.course?.title || '')
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Erro ao carregar a aula.',
        variant: 'error',
      })
    } finally {
      setIsLoading(false)
    }
  }, [courseId, lessonId, snackbar])

  useEffect(() => {
    loadData()
    // eslint-disable-next-line
  }, [lessonId])

  const navigateToLesson = useCallback(
    (targetLessonId, kind) => {
      const targetRoute =
        kind === 'exercise' ? routes.exerciseContent : routes.lessonContent
      const url = targetRoute
        .replace(':portalToken', portalToken)
        .replace(':courseId', courseId)
        .replace(':lessonId', targetLessonId)
      history.push(url)
    },
    [history, portalToken, courseId],
  )

  const handlePrevLesson = () => {
    if (lesson?.prevLessonId)
      navigateToLesson(lesson.prevLessonId, lesson.prevLessonKind)
  }

  const handleNextLesson = async () => {
    if (!lesson?.nextLessonId && !lesson?.id) return

    setIsCompleting(true)
    try {
      await service.trainingPortal.courses.completeLesson(courseId, lesson.id)
      if (lesson?.nextLessonId) {
        navigateToLesson(lesson.nextLessonId, lesson.nextLessonKind)
      } else {
        history.push(
          routes.courseCertificate
            .replace(':portalToken', portalToken)
            .replace(':courseId', courseId),
        )
      }
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Erro ao registrar progresso da aula.',
        variant: 'error',
      })
    } finally {
      setIsCompleting(false)
    }
  }

  const handleBack = () => {
    history.push(
      routes.courseDetail
        .replace(':portalToken', portalToken)
        .replace(':courseId', courseId),
    )
  }

  const content = lesson?.content
  const attachments = content?.attachments || []
  const contentBodySanitized = DOMPurify.sanitize(content?.body || '')
  const hasNextLesson = !!lesson?.nextLessonId
  const isLastLesson = !hasNextLesson

  return (
    <Box className={classes.root}>
      <Box className={classes.content}>
        <LoadingFeedback open={isLoading} />

        {!isLoading && (
          <>
            <Box className={classes.breadcrumb}>
              <IconButton
                className={classes.backButton}
                size="small"
                onClick={handleBack}
              >
                <ArrowBack fontSize="small" />
              </IconButton>
              <Typography className={classes.courseTitle}>
                {courseTitle}
              </Typography>
            </Box>

            <Box className={classes.body}>
              <Box className={classes.mainContent}>
                {content?.url && (
                  <>
                    <LessonPlayer url={content.url} />
                    <ButtonAction
                      isCompleting={isCompleting}
                      handlePrevLesson={handlePrevLesson}
                      prevLessonId={lesson?.prevLessonId}
                      handleNextLesson={handleNextLesson}
                      isLastLesson={isLastLesson}
                    />
                  </>
                )}
                {!isEmpty(attachments) && (
                  <LessonAttachments attachments={attachments} />
                )}
                <Typography className={classes.lessonTitle}>
                  {lesson?.title}
                </Typography>

                {content?.body && (
                  <Box
                    className={classes.lessonBody}
                    dangerouslySetInnerHTML={{ __html: contentBodySanitized }}
                  />
                )}

                {!content?.url && (
                  <ButtonAction
                    isCompleting={isCompleting}
                    handlePrevLesson={handlePrevLesson}
                    prevLessonId={lesson?.prevLessonId}
                    handleNextLesson={handleNextLesson}
                    isLastLesson={isLastLesson}
                  />
                )}
              </Box>
              <LessonSidebar
                modules={modules}
                activeLessonId={lessonId}
                portalToken={portalToken}
                courseId={courseId}
                history={history}
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  )
}

export default LessonContent
